import React from "react"
import { cn } from "../../utils/cn.ts"
import { AnimatePresence, motion } from "framer-motion"
import { useState, useEffect, useRef } from "react"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"

export const HoverEffect = ({
    items,
    className,
    height = 360,
}: {
    items: {
        title: string
        description: string
        link?: string
        techstack?: string
        bg?: string
        live?: boolean
    }[]
    className?: string
    height?: number
}) => {
    let [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [expanded, setExpanded] = useState(false)
    const [isMdUp, setIsMdUp] = useState(false)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const m = window.matchMedia("(min-width: 768px)")
        const update = (ev?: MediaQueryListEvent) => setIsMdUp(ev ? ev.matches : m.matches)
        update()
        if (m.addEventListener) m.addEventListener("change", update)
        else m.addListener(update)
        return () => {
            if (m.removeEventListener) m.removeEventListener("change", update)
            else m.removeListener(update)
        }
    }, [])
    type GradientKey = keyof typeof gradients
    const gradients = {
        cyanRed: "dark:from-[#24C1FF]/80 dark:to-[#D20407]/80",
        bluePurple: "dark:from-[#4F46E5]/80 dark:to-[#9333EA]/80",
        blueGreen: "dark:from-[#38BDF8]/80 dark:to-[#34D399]/80",
        kolamVerse: "dark:from-[#208a3d]/80 dark:to-[#e2f6eb]/80",
        rakuten: "dark:from-[#D8000D]/80 dark:to-[#7D001A]/80",
    }
    const updateArrowVisibility = React.useCallback(() => {
        const container = scrollContainerRef.current
        if (!container || !isMdUp) {
            setCanScrollLeft(false)
            setCanScrollRight(false)
            return
        }

        const maxScrollLeft = container.scrollWidth - container.clientWidth
        const atStart = container.scrollLeft <= 2
        const atEnd = container.scrollLeft >= maxScrollLeft - 2

        setCanScrollLeft(!atStart)
        setCanScrollRight(maxScrollLeft > 0 && !atEnd)
    }, [isMdUp])

    useEffect(() => {
        updateArrowVisibility()
        const container = scrollContainerRef.current
        if (!container) return

        container.addEventListener("scroll", updateArrowVisibility, { passive: true })
        window.addEventListener("resize", updateArrowVisibility)

        return () => {
            container.removeEventListener("scroll", updateArrowVisibility)
            window.removeEventListener("resize", updateArrowVisibility)
        }
    }, [isMdUp, items.length, updateArrowVisibility])

    const handleScroll = (direction: "left" | "right") => {
        if (!scrollContainerRef.current) return

        const container = scrollContainerRef.current
        const firstCard = container.querySelector("a") as HTMLElement | null
        const cardWidth = firstCard?.offsetWidth || 420
        const gap = Number.parseFloat(getComputedStyle(container).columnGap || getComputedStyle(container).gap || "24") || 24
        const delta = direction === "left" ? -(cardWidth + gap) : cardWidth + gap

        container.scrollBy({ left: delta, behavior: "smooth" })
    }

    return (
        <div className="relative">
            {canScrollLeft && (
                <button
                    type="button"
                    onClick={() => handleScroll("left")}
                    aria-label="Scroll projects to the left"
                    className="hidden md:flex absolute left-1 top-1/2 z-30 -translate-y-1/2 items-center justify-center rounded-full border border-cyan-400/40 bg-slate-900/80 p-3 text-cyan-300 shadow-lg shadow-cyan-400/10 backdrop-blur-sm transition hover:bg-cyan-400/10 hover:text-cyan-200"
                >
                    <FaChevronLeft />
                </button>
            )}
            {canScrollRight && (
                <button
                    type="button"
                    onClick={() => handleScroll("right")}
                    aria-label="Scroll projects to the right"
                    className="hidden md:flex absolute right-1 top-1/2 z-30 -translate-y-1/2 items-center justify-center rounded-full border border-cyan-400/40 bg-slate-900/80 p-3 text-cyan-300 shadow-lg shadow-cyan-400/10 backdrop-blur-sm transition hover:bg-cyan-400/10 hover:text-cyan-200"
                >
                    <FaChevronRight />
                </button>
            )}
            <div ref={scrollContainerRef} className={cn("flex flex-col md:flex md:flex-row md:overflow-x-auto gap-6 py-10", className)}>
                {(() => {
                    const visible = isMdUp ? items : expanded ? items : items.slice(0, 3)
                    return visible.map((item, idx) => {
                        const cardContent = (
                            <>
                                <AnimatePresence>
                                    {hoveredIndex === idx && (
                                        <motion.span
                                            className={`absolute inset-0 h-full w-full block rounded-3xl ${
                                                item?.bg ? `dark:bg-gradient-to-br ${gradients[item?.bg as GradientKey]}` : "dark:bg-cyan-400/[0.8]"
                                            }`}
                                            layoutId="hoverBackground"
                                            initial={{ opacity: 0 }}
                                            animate={{
                                                opacity: 1,
                                                transition: { duration: 0.15 },
                                            }}
                                            exit={{
                                                opacity: 0,
                                                transition: { duration: 0.15, delay: 0.2 },
                                            }}
                                        />
                                    )}
                                </AnimatePresence>
                                <Card className="h-full" live={item.live ? item.live : false}>
                                    <CardTitle>{item.title}</CardTitle>
                                    <p className="text-sm text-gray-500" style={{ whiteSpace: "pre-line" }}>
                                        {item.techstack || ""}
                                    </p>
                                    <CardDescription>{item.description}</CardDescription>
                                </Card>
                            </>
                        )

                        if (item?.link) {
                            return (
                                <a
                                    href={item.link}
                                    key={idx}
                                    className={`relative p-2 w-full md:w-[420px] md:min-w-[420px] lg:h-[${height}px] lg:shrink-0`}
                                    onMouseEnter={() => setHoveredIndex(idx)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                >
                                    {cardContent}
                                </a>
                            )
                        }

                        return (
                            <div
                                key={idx}
                                className={`relative p-2 w-full md:w-[420px] md:min-w-[420px] lg:h-[${height}px] lg:shrink-0`}
                                onMouseEnter={() => setHoveredIndex(idx)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                {cardContent}
                            </div>
                        )
                    })
                })()}
            </div>
            {/* See all / See less button - visible only on small screens (before md) */}
            {!isMdUp && items.length > 3 && (
                <div className="mt-2 text-center md:hidden">
                    <button className="text-cyan-400 font-extralight underline" onClick={() => setExpanded((s) => !s)} aria-expanded={expanded}>
                        {expanded ? "See less" : "See all"}
                    </button>
                </div>
            )}
        </div>
    )
}

export const Card = ({ className, children, live }: { className?: string; children: React.ReactNode; live: boolean }) => {
    return (
        <div
            className={cn(
                "rounded-2xl h-full w-full p-4 overflow-hidden bg-black border border-transparent dark:border-white/[0.2] group-hover:border-slate-700 relative z-20",
                className
            )}
        >
            <div className="relative z-50">
                {
                    live &&
                    <span className="absolute top-3 right-3 w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
                }
                <div className="p-4">{children}</div>
            </div>
        </div>
    )
}
export const CardTitle = ({ className, children }: { className?: string; children: React.ReactNode }) => {
    return <h4 className={cn("text-zinc-100 font-bold tracking-wide mt-4", className)}>{children}</h4>
}
export const CardDescription = ({ className, children }: { className?: string; children: React.ReactNode }) => {
    return <p className={cn("mt-8 text-zinc-400 tracking-wide leading-relaxed text-sm", className)}>{children}</p>
}
